import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import { HOUSEHOLD_APPLICATION_CATEGORY_HOUSEHOLD_APPLICATION } from 'common/householdConstants';
import { findHouseholdApplication } from 'common/householdApplication/applicationHelpers';
import {
  SR_API_ACTION_HOUSEHOLD_LISTING_UPDATE_IN_PERSON_TOUR,
  SR_API_ACTION_GET_TENANT_SCREENING_CRITERIA,
} from 'api/actionTypes';

import { SCREEN_ID_HOUSEHOLD_CONFIRM_IN_PERSON_TOUR } from 'client/constants/screenIds';
import getTenantScreeningCriteriaAction from 'client/actions/TenantScreeningCriteria/getTenantScreeningCriteria';
import { createFormValidationFn } from 'client/helpers/formikHelpers';

import branch from 'client/core/branch';
import withScreenId from 'client/helpers/withScreenId';
import withApiState from 'client/helpers/withApiState';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScrollToTop from 'client/helpers/withScrollToTop';
import postWithState from 'client/actions/postWithState';
import HouseholdActions from 'client/actions/Household';
import NavigatorActions from 'client/actions/Navigator';

import Navbar from 'client/components/Navbar';
import ButtonDeprecated, {
  BUTTON_THEME_YELLOW,
  BUTTON_THEME_GREEN_ADOBE,
  BUTTON_THEME_GREY_ADOBE,
} from 'client/components/ButtonDeprecated';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrFormikField from 'client/components/SrFormikField';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

const formField = {
  id: 'inPersonTour',
  title: 'Did you visit this home in-person?',
  validations: ['required'],
  type: 'yes_no',
};

@withTransitionHook
@withApiState(({ match }) => ({
  updateInPersonTourApiState: {
    action: SR_API_ACTION_HOUSEHOLD_LISTING_UPDATE_IN_PERSON_TOUR,
  },
  getTenantScreeningCriteriaApiState: {
    action: SR_API_ACTION_GET_TENANT_SCREENING_CRITERIA,
    queryId: match.params.listingId,
  },
}))
@branch(({ match }) => ({
  householdApplications: ['householdApplications'],
  tenantScreeningCriteria: ['tenantScreeningCriteria', 'byListingId', match.params.listingId],
}))
@withScrollToTop
@withScreenId(SCREEN_ID_HOUSEHOLD_CONFIRM_IN_PERSON_TOUR)
class HouseholdConfirmInPersonTour extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'In-Person Tour | Sunroom Rentals',
  })

  static fetchData(match, inject) {
    return getTenantScreeningCriteriaAction(inject, {
      listingId: parseInt(match.params.listingId, 10),
      includePartner: true,
      reload: true,
    });
  }

  static propTypes = {
    updateInPersonTourApiState: PropTypes.object.isRequired,
    getTenantScreeningCriteriaApiState: PropTypes.object.isRequired,
    householdApplications: PropTypes.array.isRequired,
    tenantScreeningCriteria: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    match: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);
    this.state = {
      requireInPersonTourModalOpen: false,
    };
  }

  _onRequireInPersonTourModalScheduleTour = () => {
    const { history, match } = this.props;
    history.push(`/listing/${match.params.listingId}?scrollTo=scheduleTour`);
  }

  _onRequireInPersonTourModalClose = () => {
    this.setState({
      requireInPersonTourModalOpen: false,
    });
  }

  _onFormSubmit = ({ inPersonTour }) => {
    const { householdApplications, tenantScreeningCriteria, dispatch, match } = this.props;
    const listingId = parseInt(match.params.listingId, 10);

    if (tenantScreeningCriteria?.requireInPersonTour && !inPersonTour) {
      this.setState({
        requireInPersonTourModalOpen: true,
      });
      return;
    }

    const householdApplication = findHouseholdApplication({
      householdApplications: householdApplications,
      category: HOUSEHOLD_APPLICATION_CATEGORY_HOUSEHOLD_APPLICATION,
      listingId: listingId,
    });

    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_LISTING_UPDATE_IN_PERSON_TOUR,
      payload: {
        applicationId: householdApplication.id,
        inPersonTour,
      },
    }).then(() => {
      dispatch(HouseholdActions.roommateApplicationStep, {
        tourId: parseInt(match.params.tourId, 10),
        listingId: parseInt(match.params.listingId, 10),
      });
    });
  }

  _onBack = () => {
    const {
      match: { params },
      dispatch,
    } = this.props;

    dispatch(NavigatorActions.replace, {
      to: `/listing/${params.listingId}`,
    });
  }

  render() {
    const {
      updateInPersonTourApiState,
      getTenantScreeningCriteriaApiState,
    } = this.props;

    const {
      requireInPersonTourModalOpen,
    } = this.state;

    return (
      <div className='page-inviteRoommates'>
        <div className='inviteRoommates'>
          <Navbar
            leftButton={(
              <div
                className='link back'
                onClick={this._onBack}
              />
            )}
          />
          <div className='stepContainer'>
            <div className='formContainer answers'>
              <Formik
                enableReinitialize
                validate={createFormValidationFn([formField])}
                onSubmit={this._onFormSubmit}
                render={(formikBag) => (
                  <Form>
                    <SrFormikField
                      formField={formField}
                      formikBag={formikBag}
                    />
                    <ButtonDeprecated
                      theme={BUTTON_THEME_YELLOW}
                      type='submit'
                      disabled={
                        !formikBag.isValid
                        || updateInPersonTourApiState.inProgress
                        || getTenantScreeningCriteriaApiState.inProgress
                      }
                    >
                      {updateInPersonTourApiState.inProgress ? 'please wait...' : 'next'}
                    </ButtonDeprecated>
                  </Form>
                )}
              />
            </div>
          </div>
        </div>
        {requireInPersonTourModalOpen && (
          <SrSimpleMessageModal
            iconUrl='/images/wood-door.svg'
            headline='You must tour this property in-person before applying'
            underline
            message={
              <>
                <SrText
                  weight='700'
                  color='blueGreycliff'
                >
                  Use the button below to schedule a tour!
                </SrText>
                <SrBox paddingTop='20px'>
                  <SrText
                    color='blueGreycliff'
                    weight='500'
                  >
                    {
                      'Per the landlord’s policy you won’t be able to receive an application ' +
                      'refund if you choose not to tour the property.'
                    }
                  </SrText>
                </SrBox>
              </>
            }
            multiButtons={[
              {
                buttonText: 'Schedule Tour',
                buttonTheme: BUTTON_THEME_GREEN_ADOBE,
                onButtonClick: this._onRequireInPersonTourModalScheduleTour,
              },
              {
                buttonText: 'Cancel',
                buttonTheme: BUTTON_THEME_GREY_ADOBE,
                onButtonClick: this._onRequireInPersonTourModalClose,
              },
            ]}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            maxWidth={375}
          />
        )}
      </div>
    );
  }
}

HouseholdConfirmInPersonTour.displayName = 'HouseholdConfirmInPersonTour';

export default HouseholdConfirmInPersonTour;
